import {
  createDefaultChartChoiceOptions,
  createDefaultDimensionValuesChoicesFromIdOrValues,
  createDefaultChartVariantValueChoiceFromIdOrValue,
  createDefaultChartVariantValuesChoiceFromIdOrValue,
  populateChoices,
  generateAllTransposedMetricsDefinitions,
  createDefaultDimensionValueChoicesFromIdOrValues,
  generateAllVariantMetricsDefinitions,
  extractIdFromChoiceId,
  getColumnsAndChoiceFromColumnsProp
} from './choices'
import { createChartChoiceValueOptions } from './options'
import { assertNever, strictObjectEntries, strictObjectValues, errorLocal, warningLocal } from './generics'
import type { ThemeColor } from '@precis-digital/kurama/theme'
import type {
  AxisType,
  ChartChoice,
  ChartChoiceChartAxis,
  ChartChoicesMap,
  ChartColumnDimensionValuesChoice,
  ChartDimensionChoice,
  ChartDimensionValueChoice,
  ChartDimensionValueOfChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartMetricChoice,
  ChartMetricsChoice,
  ChartMetricTransposedChoice,
  ChartMetricVariantsChoice,
  ChartMetricVariantsTransposedChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  DimensionAxisType,
  DimensionsMap,
  DimensionsValuesMap,
  ElementDescription,
  ElementDescriptionDimension,
  ElementDescriptionDimensionChoice,
  ElementDescriptionMetric,
  ElementDescriptionMetricChoice,
  ElementsDescriptionsMap,
  FinaliseDimensionValuesResult,
  FinaliseMetricsResult,
  MetricChoice,
  MetricsMap,
  MetricsVariantsMap
} from '../internalTypes'
import type {
  ChartCardChartDefinition,
  ChartCardChartProps,
  BaseElementDescription,
  CartesianChartType,
  ChartDataRow,
  ChartDimensionValues,
  ChartMetric,
  ChartMetricOrMetrics,
  ChartMetrics,
  ChartMetricTransposed,
  ChartMetricVariants,
  ChartType,
  ChartVariantValue,
  ChartVariantValues,
  ColumnsProps,
  DrawingInnerProps,
  FirstAxisProps,
  LayoutType,
  OtherSecondAxisProps,
  PolarChartType,
  ScatterChartType,
  SecondAxisProps,
  ThirdAxisProps,
  VariantIdOrValue,
  VariantIdOrValues
} from '../types'
import { getColumnDataKey, getColumnId } from '../components/table/utils/columnKeys'
import { TableColumnsChoice, TableColumnValuesChoice } from '../constants'
import type { ChartCardTableColumnDef } from '../components/table/tanstack'

const variantFromMetrics = <DataKey extends string>(
  metrics: DataKey[],
  metricsVariantMap: MetricsVariantsMap<DataKey>
): DataKey | undefined =>
  metrics.map<DataKey | undefined>((metric) => metricsVariantMap[metric]).find((variantId) => variantId != null)

const chartMetricFromId = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  id,
  newId,
  metricsMap,
  metricsVariantMap,
  dimensionsMap
}: {
  id: DataKey
  newId: string
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsVariantMap: MetricsVariantsMap<DataKey>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
}): ChartMetric<'props', DataKey, DataRow> | undefined => {
  const variant = metricsVariantMap[id]
  const metric = metricsMap[id]

  if (metric != null) {
    return {
      type: 'metric',
      id: newId as DataKey,
      change: {
        change: 'controlled',
        options: [id],
        display: metric.color != null || metric.pattern != null
      },
      label: metric.label,
      description: metric.description,
      color: metric.color,
      pattern: metric.pattern
    }
  }

  if (variant == null) {
    errorLocal(`chartMetricFromId ${id} for ${newId} not a valid metric`, {
      id,
      newId,
      metricsMap,
      metricsVariantMap,
      dimensionsMap,
      metric,
      variant
    })
    return undefined
  }

  const variantDefinition = dimensionsMap[variant]

  if (variantDefinition?.type !== 'variant') {
    errorLocal(`chartMetricFromId ${id} for ${newId} invalid, variantDefinition is null or invalid`, {
      id,
      variant,
      newId,
      metricsMap,
      metricsVariantMap,
      dimensionsMap,
      variantDefinition
    })
    return undefined
  }

  const newMetric = variantDefinition.newMetrics[id]

  if (newMetric == null) {
    errorLocal(`chartMetricFromId ${id} for ${newId} invalid, newMetric is null`, {
      id,
      variant,
      newId,
      metricsMap,
      metricsVariantMap,
      dimensionsMap,
      variantDefinition
    })
    return undefined
  }

  return {
    type: 'metric',
    id: newId as DataKey,
    change: {
      change: 'controlled',
      options: [id],
      display: newMetric.color != null || newMetric.pattern != null
    },
    variant,
    label: newMetric.label,
    description: newMetric.description,
    color: newMetric.color,
    pattern: newMetric.pattern
  }
}

const validateIds = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  ids,
  metricsMap,
  metricsVariantMap
}: {
  ids: DataKey[]
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsVariantMap: MetricsVariantsMap<DataKey>
}): boolean => {
  const invalidIds = ids.filter((id) => !(id in metricsMap || id in metricsVariantMap))

  if (invalidIds.length > 0) {
    errorLocal(`chartMetricFromIds some of the Ids are not metrics: ${invalidIds.join(', ')}`, {
      ids,
      invalidIds,
      metricsMap,
      metricsVariantMap
    })
    return false
  }

  return true
}

const chartMetricFromIds = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  ids,
  newId,
  metricsMap,
  metricsVariantMap
}: {
  ids: DataKey[]
  newId: string
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsVariantMap: MetricsVariantsMap<DataKey>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
}): ChartMetrics<'props', DataKey, DataRow> | undefined => {
  if (!validateIds({ ids, metricsMap, metricsVariantMap })) {
    return undefined
  }

  return {
    type: 'metrics',
    id: newId as DataKey,
    change: {
      change: 'controlled',
      options: ids,
      display: true
    }
  }
}

const validateVariant = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chartMetricOrMetrics,
  metricsOptions,
  metricsVariantMap,
  otherChoicesMap
}: {
  chartMetricOrMetrics: ChartMetricOrMetrics<'props', DataKey, DataRow>
  metricsOptions: DataKey[]
  metricsVariantMap: MetricsVariantsMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}):
  | {
      type: 'metric' | 'metricTransposed' | 'metrics'
      variantIdOrValue: VariantIdOrValue<'props', DataKey, DataRow> | undefined
      variantIdOrValues?: never
    }
  | {
      type: 'metricVariants'
      variantIdOrValue?: never
      variantIdOrValues: VariantIdOrValues<'props', DataKey, DataRow> | undefined
    }
  | { type: 'invalid'; variantIdOrValue?: never; variantIdOrValues?: never } => {
  // Validate the variant from the metrics options against the variant in the props
  const { type, variant: variantOr } = chartMetricOrMetrics

  const metricsVariant = variantFromMetrics(metricsOptions, metricsVariantMap)

  if (metricsVariant == null && variantOr != null) {
    errorLocal(`finaliseMetrics ${type} invalid variantOr != null without metricsVariant`, { variantOr })
    return { type: 'invalid' }
  }

  const variantOrChoice =
    (otherChoicesMap[variantOr] as ChartVariantValueChoice<DataKey, DataRow> | undefined) ?? variantOr

  if (metricsVariant != null && variantOrChoice != null) {
    if (typeof variantOrChoice === 'string') {
      if (extractIdFromChoiceId(variantOrChoice) !== metricsVariant) {
        errorLocal(`finaliseMetrics ${type} invalid variantOrChoice !== metricsVariant`, {
          variantOrChoice,
          metricsVariant
        })
        return { type: 'invalid' }
      }
    } else if (extractIdFromChoiceId(variantOrChoice.dimension) !== metricsVariant) {
      errorLocal(`finaliseMetrics ${type} invalid variantOrChoice.dimension !== metricsVariant`, {
        variantOrChoice,
        metricsVariant
      })
      return { type: 'invalid' }
    }
  }

  if (type === 'metricVariants') {
    return { type, variantIdOrValues: variantOr ?? metricsVariant }
  }

  return { type, variantIdOrValue: variantOr ?? metricsVariant }
}

const finaliseMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  dimensionAxis,
  chartMetric,
  variantIdOrValue,
  dimensionsMap,
  metricsMap,
  metricsOptions,
  otherChoicesMap
}: {
  choiceAxis: ChartChoiceChartAxis
  dimensionAxis: DimensionAxisType
  chartMetric: ChartMetric<'props', DataKey, DataRow>
  variantIdOrValue: VariantIdOrValue<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsOptions: DataKey[]
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): FinaliseMetricsResult<DataKey, DataRow> => {
  const { type, id, change: metricsChange, label, description, color, pattern } = chartMetric

  const choices: Array<
    | ChartMetricChoice<DataKey, DataRow> // Choose one metric.
    | ChartVariantValueChoice<DataKey, DataRow> // Choose one variant value. If one of the metrics options is a variant
  > = []

  const { variantChoice, newMetrics } = createDefaultChartVariantValueChoiceFromIdOrValue<DataKey, DataRow>({
    parentKey: id,
    choiceAxis,
    variantIdOrValue,
    dimensionsMap,
    otherChoicesMap
  })

  if (variantIdOrValue != null) {
    if (variantChoice == null) {
      errorLocal(`finaliseMetric ${type} invalid variantIdOrValue != null without variantChoice`, {
        variantIdOrValue,
        choiceAxis,
        dimensionsMap,
        otherChoicesMap
      })
      return { type: 'invalid' }
    }

    if (variantChoice.type === 'variantValue') {
      choices.push(variantChoice)
    } else if (variantChoice.axis !== dimensionAxis) {
      errorLocal(
        `finaliseMetric ${choiceAxis.axis} ${type} invalid variantChoice type !== 'variantValue' and axis !== dimensionAxis`,
        {
          variantChoice,
          dimensionAxis
        }
      )
      return { type: 'invalid' }
    }
  }

  const metricChoice: ChartMetricChoice<DataKey, DataRow> = {
    ...choiceAxis,
    type: 'metric',
    id,
    variant: variantChoice?.id,
    change: {
      ...metricsChange,
      options: createDefaultChartChoiceOptions(metricsOptions, metricsMap, newMetrics)
    },
    label,
    description,
    color,
    pattern
  }

  choices.push(metricChoice)

  return {
    id,
    type,
    choices
  }
}

const finaliseMetricVariants = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  chartMetricVariants,
  variantIdOrValues,
  dimensionsMap,
  metricsMap,
  dimensionsValuesMap,
  data,
  metricsOptions,
  otherChoicesMap,
  chartAvailableColors
}: {
  choiceAxis: ChartChoiceChartAxis
  chartMetricVariants: ChartMetricVariants<'props', DataKey, DataRow>
  variantIdOrValues: VariantIdOrValues<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  metricsOptions: DataKey[]
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseMetricsResult<DataKey, DataRow> => {
  const { type, id, change: metricsChange, label, description, color, pattern, filter } = chartMetricVariants

  const choices: Array<
    | ChartMetricVariantsChoice<DataKey, DataRow> // Choose one metric variant.
    | ChartVariantValuesChoice<DataKey, DataRow> // Choose multiple variant values.
    | ChartDimensionValueChoice<DataKey, DataRow> // Choose single dimension value. If the transpose dimension is a fixed value
    | ChartDimensionChoice<DataKey, DataRow> // Choose one dimension. If the transpose dimension is a variable
    | ChartDimensionValueOfChoice<DataKey, DataRow> // Choose single dimension value. If the transpose dimension is a variable
  > = []

  if (variantIdOrValues == null) {
    errorLocal(`finaliseMetrics ${type} invalid variantIdOrValues == null`)
    return { type: 'invalid' }
  }

  const {
    variantChoice,
    newMetrics,
    fullMetricsList: variantMetricsList
  } = createDefaultChartVariantValuesChoiceFromIdOrValue<DataKey, DataRow>({
    parentKey: id,
    choiceAxis,
    variantIdOrValues,
    dimensionsMap,
    otherChoicesMap
  })

  if (variantChoice == null) {
    errorLocal(`finaliseMetrics ${type} invalid variantChoice == null`, { variantIdOrValues })
    return { type: 'invalid' }
  }

  choices.push(variantChoice)

  const fullMetricsList =
    variantMetricsList == null
      ? metricsOptions
      : metricsOptions.filter((option) => !(option in newMetrics)).concat(variantMetricsList)

  let transpose: DataKey | undefined
  let transposeDimensionChoice: ChartDimensionChoice<DataKey, DataRow> | undefined
  let transposeDimensionValueChoice:
    | ChartDimensionValueChoice<DataKey, DataRow>
    | ChartDimensionValueOfChoice<DataKey, DataRow>
    | undefined

  if (chartMetricVariants.transpose != null) {
    const {
      type: dimensionType,
      dimension,
      dimensionChoice,
      dimensionValueChoice
    } = createDefaultDimensionValueChoicesFromIdOrValues<DataKey, DataRow>({
      parentKey: id,
      choiceAxis,
      metrics: fullMetricsList,
      dimensionIdOrValue: chartMetricVariants.transpose,
      dimensionsMap,
      dimensionsValuesMap,
      otherChoicesMap,
      data,
      chartAvailableColors
    })

    if (dimensionType === 'invalid') {
      errorLocal(`finaliseMetrics ${type} invalid dimensionValueChoicesFromIdOrValues`, {
        chartMetrics: chartMetricVariants
      })
      return { type: 'invalid' }
    }

    transposeDimensionChoice = dimensionChoice
    transposeDimensionValueChoice = dimensionValueChoice

    if (dimensionType === 'dimension') {
      transpose = dimension
    } else {
      transpose = dimensionType === 'value' ? dimensionValueChoice.id : dimensionValueChoice?.id ?? dimensionChoice.id
    }
  }

  choices.push({
    ...choiceAxis,
    type: 'metricVariants',
    id,
    variant: variantChoice.id,
    transpose,
    change: {
      ...metricsChange,
      options: createDefaultChartChoiceOptions(metricsOptions, metricsMap, newMetrics)
    },
    label,
    description,
    color,
    pattern,
    filter
  })

  if (transposeDimensionChoice != null) {
    // Choosing the dimension to be transposed - {@link ChartDimensionChoice}
    choices.push(transposeDimensionChoice)
  }

  // Choosing the dimension values to be transposed - {@link ChartDimensionValueChoice} or {@link ChartDimensionValueOfChoice}
  if (transposeDimensionValueChoice != null) {
    choices.push(transposeDimensionValueChoice)
  }

  return {
    id,
    type,
    choices
  }
}

const finaliseMetricTransposed = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  dimensionAxis,
  chartMetricTransposed,
  variantIdOrValue,
  dimensionsMap,
  metricsMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  metricsOptions,
  chartAvailableColors
}: {
  choiceAxis: ChartChoiceChartAxis
  dimensionAxis: DimensionAxisType
  chartMetricTransposed: ChartMetricTransposed<'props', DataKey, DataRow>
  variantIdOrValue: DataKey | ChartVariantValue<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  metricsOptions: DataKey[]
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseMetricsResult<DataKey, DataRow> => {
  const { type, id, change: metricsChange, label, description, color, pattern } = chartMetricTransposed

  const choices: Array<
    | ChartMetricTransposedChoice<DataKey, DataRow> // Choose one metric to be transposed
    | ChartVariantValueChoice<DataKey, DataRow> // Choose one variant value. If one of the metrics options is a variant
    | ChartDimensionValuesChoice<DataKey, DataRow> // Choose multiple dimension values. If the transpose dimension is a fixed value
    | ChartDimensionChoice<DataKey, DataRow> // Choose one dimension. If the transpose dimension is a variable
    | ChartDimensionValuesOfChoice<DataKey, DataRow> // Choose multiple dimension values. If the transpose dimension is a variable
  > = []

  const {
    variantChoice,
    newMetrics,
    fullMetricsList: variantMetricsList
  } = createDefaultChartVariantValueChoiceFromIdOrValue<DataKey, DataRow>({
    parentKey: id,
    choiceAxis,
    variantIdOrValue,
    dimensionsMap,
    otherChoicesMap
  })

  if (variantIdOrValue != null) {
    if (variantChoice == null) {
      errorLocal(
        `finaliseMetricTransposed ${choiceAxis.axis} ${type} invalid variantIdOrValue != null without variantChoice`,
        {
          variantIdOrValue,
          otherChoicesMap
        }
      )
      return { type: 'invalid' }
    }

    if (variantChoice.type === 'variantValue') {
      choices.push(variantChoice)
    } else if (variantChoice.axis !== dimensionAxis) {
      errorLocal(
        `finaliseMetricTransposed ${choiceAxis.axis} ${type} invalid variantChoice type !== 'variantValue' and axis !== dimensionAxis`,
        {
          variantChoice,
          dimensionAxis
        }
      )
      return { type: 'invalid' }
    }
  }

  const fullMetricsList =
    variantMetricsList == null
      ? metricsOptions
      : metricsOptions.filter((option) => !(option in newMetrics)).concat(variantMetricsList)

  const {
    type: dimensionType,
    dimension,
    dimensionChoice,
    dimensionValuesChoice
  } = createDefaultDimensionValuesChoicesFromIdOrValues<DataKey, DataRow, false>({
    parentKey: id,
    choiceAxis,
    metrics: fullMetricsList,
    dimensionIdOrValues: chartMetricTransposed.transpose,
    dimensionsMap,
    dimensionsValuesMap,
    otherChoicesMap,
    data,
    chartAvailableColors
  })

  if (dimensionType === 'invalid') {
    errorLocal(`finaliseMetrics ${type} invalid dimensionValuesChoicesFromIdOrValues`, {
      parentKey: id,
      choiceAxis,
      metrics: fullMetricsList,
      dimensionIdOrValues: chartMetricTransposed.transpose,
      dimensionsMap,
      dimensionsValuesMap,
      otherChoicesMap,
      data
    })
    return { type: 'invalid' }
  }

  const transpose =
    dimensionType === 'dimension'
      ? dimension
      : dimensionType === 'values'
      ? dimensionValuesChoice.id
      : dimensionValuesChoice?.id ?? dimensionChoice.id

  choices.push({
    ...choiceAxis,
    type,
    id,
    variant: variantChoice?.id,
    transpose,
    change: {
      ...metricsChange,
      options: createDefaultChartChoiceOptions(metricsOptions, metricsMap, newMetrics)
    },
    label,
    description,
    color,
    pattern
  })

  if (dimensionChoice != null) {
    // Choosing the dimension to be transposed - {@link ChartDimensionChoice}
    choices.push(dimensionChoice)
  }

  if (dimensionValuesChoice != null) {
    // Choosing the dimension values to be transposed - {@link ChartDimensionValuesChoice} or {@link ChartDimensionValuesOfChoice}
    choices.push(dimensionValuesChoice)
  }

  return {
    id,
    type,
    choices
  }
}

const finaliseMetrics = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  chartMetrics,
  variantIdOrValue,
  dimensionsMap,
  metricsMap,
  metricsOptions,
  otherChoicesMap
}: {
  choiceAxis: ChartChoiceChartAxis
  chartMetrics: ChartMetrics<'props', DataKey, DataRow>
  variantIdOrValue: DataKey | ChartVariantValue<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsOptions: DataKey[]
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): FinaliseMetricsResult<DataKey, DataRow> => {
  const { type, id, change: metricsChange, label, description, color, pattern, limit, selected } = chartMetrics

  const choices: Array<
    | ChartMetricsChoice<DataKey, DataRow> // Choose multiple metrics.
    | ChartVariantValueChoice<DataKey, DataRow> // Choose one variant value. If one of the metrics options is a variant
  > = []

  const { variantChoice, newMetrics } = createDefaultChartVariantValueChoiceFromIdOrValue<DataKey, DataRow>({
    parentKey: id,
    choiceAxis,
    variantIdOrValue,
    dimensionsMap,
    otherChoicesMap
  })

  if (variantIdOrValue != null) {
    if (variantChoice == null) {
      errorLocal(`finaliseMetrics ${type} invalid variantIdOrValue != null without variantChoice`, {
        variantIdOrValue
      })
      return { type: 'invalid' }
    }

    if (variantChoice.type === 'variantValue') {
      choices.push(variantChoice)
    } else {
      errorLocal(`finaliseMetrics ${choiceAxis.axis} ${type} invalid variantChoice type !== 'variantValue'`, {
        variantChoice
      })
      return { type: 'invalid' }
    }
  }

  const metricsChoice: ChartMetricsChoice<DataKey, DataRow> = {
    ...choiceAxis,
    type: 'metrics',
    id,
    variant: variantChoice?.id,
    change: {
      ...metricsChange,
      options: createDefaultChartChoiceOptions(metricsOptions, metricsMap, newMetrics)
    },
    label,
    description,
    color,
    pattern,
    ...(limit == null ? undefined : { limit }),
    ...(selected == null ? undefined : { selected })
  }

  choices.push(metricsChoice)

  return {
    id,
    type,
    choices
  }
}

const finaliseMetricOrMetrics = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  dimensionAxis,
  chartMetricOrMetrics,
  dimensionsMap,
  metricsMap,
  metricsVariantMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  choiceAxis: ChartChoiceChartAxis
  dimensionAxis: DimensionAxisType
  chartMetricOrMetrics: ChartMetricOrMetrics<'props', DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsVariantMap: MetricsVariantsMap<DataKey>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseMetricsResult<DataKey, DataRow> => {
  if (
    dimensionsMap == null ||
    metricsMap == null ||
    metricsVariantMap == null ||
    dimensionsValuesMap == null ||
    data == null
  ) {
    errorLocal(`finaliseMetricOrMetrics invalid missing properties`, {
      dimensionsMap,
      metricsMap,
      metricsVariantMap,
      dimensionsValuesMap,
      data
    })
    return { type: 'invalid' }
  }

  const { type, id, change: metricsChange } = chartMetricOrMetrics

  if (id in dimensionsMap || id in metricsMap || id in metricsVariantMap) {
    errorLocal(`finaliseMetricOrMetrics ${type} invalid id in dimensionsMap or metricsMap or metricsVariantMap`, {
      id,
      dimensionsMap,
      metricsMap,
      metricsVariantMap
    })
    return { type: 'invalid' }
  }

  const { options: metricsOptions } = metricsChange

  if (!metricsOptions.every((option) => option in metricsMap || option in metricsVariantMap)) {
    errorLocal(`finaliseMetricOrMetrics ${type} invalid metricsOptions not in metricsMap or metricsVariantMap`, {
      metricsOptions,
      metricsMap,
      metricsVariantMap
    })
    return { type: 'invalid' }
  }

  const {
    type: validatedVariantMetricsType,
    variantIdOrValue,
    variantIdOrValues
  } = validateVariant({
    chartMetricOrMetrics,
    metricsOptions,
    metricsVariantMap,
    otherChoicesMap
  })

  if (validatedVariantMetricsType === 'invalid' || validatedVariantMetricsType !== type) {
    errorLocal(`finaliseMetricOrMetrics ${type} invalid validatedVariantMetricsType !== type`, {
      validatedVariantMetricsType
    })
    return { type: 'invalid' }
  }

  if (type === 'metric') {
    return finaliseMetric({
      choiceAxis,
      dimensionAxis,
      chartMetric: chartMetricOrMetrics,
      variantIdOrValue,
      dimensionsMap,
      metricsMap,
      metricsOptions,
      otherChoicesMap
    })
  }

  if (type === 'metricVariants') {
    return finaliseMetricVariants({
      chartMetricVariants: chartMetricOrMetrics,
      variantIdOrValues,
      choiceAxis,
      dimensionsMap,
      metricsMap,
      dimensionsValuesMap,
      data,
      metricsOptions,
      otherChoicesMap,
      chartAvailableColors
    })
  }

  if (type === 'metricTransposed') {
    return finaliseMetricTransposed({
      choiceAxis,
      dimensionAxis,
      chartMetricTransposed: chartMetricOrMetrics,
      variantIdOrValue,
      dimensionsMap,
      metricsMap,
      dimensionsValuesMap,
      otherChoicesMap,
      data,
      metricsOptions,
      chartAvailableColors
    })
  }

  if (type === 'metrics') {
    return finaliseMetrics({
      choiceAxis,
      chartMetrics: chartMetricOrMetrics,
      variantIdOrValue,
      dimensionsMap,
      metricsMap,
      metricsOptions,
      otherChoicesMap
    })
  }

  return assertNever(type)
}

const finaliseAxisDimensionValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choiceAxis,
  axis,
  dimensionsMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  choiceAxis: ChartChoiceChartAxis
  axis:
    | DataKey
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
    | ChartVariantValues<'props', DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseDimensionValuesResult<DataKey, DataRow> => {
  const { type, dimension, dimensionChoice, dimensionValuesChoice } = createDefaultDimensionValuesChoicesFromIdOrValues<
    DataKey,
    DataRow,
    true
  >({
    parentKey: undefined,
    choiceAxis,
    metrics: undefined,
    dimensionIdOrValues: axis,
    dimensionsMap,
    dimensionsValuesMap,
    otherChoicesMap,
    data,
    chartAvailableColors
  })

  const { chartType } = choiceAxis

  if (type === 'invalid') {
    errorLocal(`finaliseAxisDimensionValues ${chartType} invalid dimensionValuesChoicesFromIdOrValues`, { axis })
    return { type: 'invalid' }
  }

  if (type === 'dimension') {
    return {
      id: dimension,
      type: 'dimension',
      choices: []
    }
  }

  if (type === 'values') {
    return {
      id: dimensionValuesChoice.id,
      type: 'values',
      choices: [dimensionValuesChoice]
    }
  }

  if (type === 'variantValues') {
    return {
      id: dimensionValuesChoice.id,
      type: 'variantValues',
      choices: [dimensionValuesChoice]
    }
  }

  if (dimensionValuesChoice == null) {
    return {
      id: dimensionChoice.id,
      type: 'valuesOf',
      choices: [dimensionChoice]
    }
  }

  return {
    id: dimensionValuesChoice.id,
    type: 'valuesOf',
    choices: [dimensionChoice, dimensionValuesChoice]
  }
}

export const finaliseFirstAxis = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  chartType: ChartType,
  axis: FirstAxisProps<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow> | undefined,
  metricsMap: MetricsMap<DataKey, DataRow> | undefined,
  metricsVariantMap: MetricsVariantsMap<DataKey> | undefined,
  dimensionsValuesMap: DimensionsValuesMap<DataKey> | undefined,
  data: DataRow[] | null | undefined,
  chartAvailableColors: readonly ThemeColor[]
): FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow> => {
  if (axis == null) {
    errorLocal(`finaliseFirstAxis ${chartType} axis null`)
    return { type: 'undefined' }
  }

  const choiceAxis: ChartChoiceChartAxis = { axis: 'first', chartType }

  if (
    dimensionsMap == null ||
    metricsMap == null ||
    metricsVariantMap == null ||
    dimensionsValuesMap == null ||
    data == null
  ) {
    errorLocal(`finaliseFirstAxis ${chartType} missing requirements`, {
      dimensionsMap,
      metricsMap,
      metricsVariantMap,
      dimensionsValuesMap,
      data
    })
    return { type: 'invalid' }
  }

  if (chartType === 'scatter') {
    let chartMetric: ChartMetric<'props', DataKey, DataRow> | undefined
    if (typeof axis === 'string') {
      chartMetric = chartMetricFromId<DataKey, DataRow>({
        id: axis,
        newId: 'first-axis-metric',
        metricsMap,
        metricsVariantMap,
        dimensionsMap
      })
      if (chartMetric == null) {
        errorLocal(`finaliseFirstAxis ${chartType} axis string chartMetric null`, {
          axis,
          metricsMap,
          metricsVariantMap
        })
        return { type: 'invalid' }
      }
    } else {
      if (axis.type !== 'metric') {
        errorLocal(`finaliseFirstAxis ${chartType} axis.type !== 'metric'`, { axis })
        return { type: 'invalid' }
      }
      chartMetric = axis
    }

    return finaliseMetricOrMetrics<DataKey, DataRow>({
      choiceAxis,
      dimensionAxis: 'third',
      chartMetricOrMetrics: chartMetric,
      dimensionsMap,
      metricsMap,
      metricsVariantMap,
      dimensionsValuesMap,
      otherChoicesMap: {},
      data,
      chartAvailableColors
    })
  }

  if (typeof axis !== 'string' && axis.type === 'metric') {
    errorLocal(`finaliseFirstAxis ${chartType} axis.type === 'metric'`, { axis })
    return { type: 'invalid' }
  }

  return finaliseAxisDimensionValues<DataKey, DataRow>({
    choiceAxis,
    axis,
    dimensionsMap,
    dimensionsValuesMap,
    otherChoicesMap: {},
    data,
    chartAvailableColors
  })
}

export const finaliseSecondAxis = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisChoice,
  chartType,
  axis: axisProp,
  dimensionsMap,
  metricsMap,
  metricsVariantMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  axisChoice: AxisType
  chartType: ChartType | undefined
  axis: SecondAxisProps<DataKey, DataRow> | OtherSecondAxisProps<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow> | undefined
  metricsMap: MetricsMap<DataKey, DataRow> | undefined
  metricsVariantMap: MetricsVariantsMap<DataKey> | undefined
  dimensionsValuesMap: DimensionsValuesMap<DataKey> | undefined
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[] | null | undefined
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseMetricsResult<DataKey, DataRow> => {
  if (axisProp == null) {
    return { type: 'undefined' }
  }

  if (chartType == null) {
    errorLocal(`useValidateChartProps finaliseSecondAxis chartType null`, { axis: axisProp, chartType })
    return { type: 'invalid' }
  }

  const choiceAxis: ChartChoiceChartAxis = { axis: axisChoice, chartType }

  if (
    dimensionsMap == null ||
    metricsMap == null ||
    metricsVariantMap == null ||
    dimensionsValuesMap == null ||
    data == null
  ) {
    errorLocal(`useValidateChartProps finaliseSecondAxis ${chartType} missing requirements`, {
      dimensionsMap,
      metricsMap,
      metricsVariantMap,
      dimensionsValuesMap,
      data
    })
    return { type: 'invalid' }
  }

  let axis: ChartMetricOrMetrics<'props', DataKey, DataRow> | undefined

  if (typeof axisProp === 'string') {
    axis = chartMetricFromId<DataKey, DataRow>({
      id: axisProp,
      newId: `${axisChoice}-axis-metric`,
      metricsMap,
      metricsVariantMap,
      dimensionsMap
    })
    if (axis == null) {
      errorLocal(`useValidateChartProps finaliseSecondAxis ${chartType} axis string chartMetric null`, {
        axis,
        metricsMap,
        metricsVariantMap
      })
      return { type: 'invalid' }
    }
  } else if (Array.isArray(axisProp)) {
    axis = chartMetricFromIds<DataKey, DataRow>({
      ids: axisProp,
      newId: `${axisChoice}-axis-metrics`,
      metricsMap,
      metricsVariantMap,
      dimensionsMap
    })
    if (axis == null) {
      errorLocal(`useValidateChartProps finaliseSecondAxis ${chartType} axis string[] chartMetric null`, {
        axis,
        metricsMap,
        metricsVariantMap
      })
      return { type: 'invalid' }
    }
  } else {
    axis = axisProp
  }

  if (chartType === 'scatter') {
    if (axis.type !== 'metric') {
      errorLocal(`useValidateChartProps finaliseSecondAxis ${chartType} axis.type !== 'metric'`, { axis })
      return { type: 'invalid' }
    }
  }

  return finaliseMetricOrMetrics<DataKey, DataRow>({
    choiceAxis,
    dimensionAxis: chartType === 'scatter' ? 'third' : 'first',
    chartMetricOrMetrics: axis,
    dimensionsMap,
    metricsMap,
    metricsVariantMap,
    dimensionsValuesMap,
    otherChoicesMap,
    data,
    chartAvailableColors
  })
}

export const finaliseThirdAxis = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chartType,
  axis,
  dimensionsMap,
  metricsMap,
  metricsVariantMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  chartType: ChartType
  axis: ThirdAxisProps<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow> | undefined
  metricsMap: MetricsMap<DataKey, DataRow> | undefined
  metricsVariantMap: MetricsVariantsMap<DataKey> | undefined
  dimensionsValuesMap: DimensionsValuesMap<DataKey> | undefined
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[] | null | undefined
  chartAvailableColors: readonly ThemeColor[]
}): FinaliseDimensionValuesResult<DataKey, DataRow> => {
  if (axis == null) {
    return { type: chartType === 'scatter' ? 'invalid' : 'undefined' }
  }

  if (chartType !== 'scatter') {
    return { type: 'invalid' }
  }

  const choiceAxis: ChartChoiceChartAxis = { axis: 'third', chartType }

  if (
    dimensionsMap == null ||
    metricsMap == null ||
    metricsVariantMap == null ||
    dimensionsValuesMap == null ||
    data == null
  ) {
    return { type: 'invalid' }
  }

  return finaliseAxisDimensionValues<DataKey, DataRow>({
    choiceAxis,
    axis,
    dimensionsMap,
    dimensionsValuesMap,
    otherChoicesMap,
    data,
    chartAvailableColors
  })
}

export const finaliseChoicesTable = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columnsProp,
  dimensionsMap,
  metricsMap,
  dimensionsValuesMap,
  choicesList,
  partialChoicesMap,
  data,
  chartAvailableColors
}: {
  columnsProp: ColumnsProps<DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  metricsVariantMap: MetricsVariantsMap<DataKey> // handle variants in Table ?
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  choicesList: DataKey[]
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): Array<ChartCardTableColumnDef<DataKey, DataRow>> => {
  const { columns, columnsChoice } = getColumnsAndChoiceFromColumnsProp<DataKey, DataRow>({
    columnsProp,
    dimensionsMap,
    metricsMap,
    data
  })
  choicesList.push(columnsChoice.id)
  partialChoicesMap[columnsChoice.id] = columnsChoice

  for (const column of columns) {
    const id = getColumnId(column)
    const dataKey = getColumnDataKey<DataKey, DataRow>(column)
    if (dataKey != null) {
      const sort = column.meta?.chartCard?.sort
      const dimension = dimensionsMap[dataKey]
      const metric = metricsMap[dataKey]
      if (dimension != null) {
        if (dimension.type === 'variant') {
          // handle variant dimensions in Table ?
          continue
        }
        const choiceId = `${TableColumnValuesChoice}.${id}` as DataKey
        const choice: ChartColumnDimensionValuesChoice<DataKey, DataRow> = {
          axis: 'second',
          chartType: 'table',
          type: TableColumnValuesChoice,
          id: choiceId,
          dimension: dataKey,
          change: {
            change: 'internal',
            options: createChartChoiceValueOptions({
              dimension,
              otherOption: undefined,
              options: Array.from(dimensionsValuesMap[dataKey]),
              sort: sort === false ? undefined : sort,
              chartAvailableColors
            })
          }
        }
        partialChoicesMap[choiceId] = choice
        choicesList.push(choiceId)
      } else if (metric != null) {
        warningLocal(`finaliseChoicesTable: metric column not yet handled`, { column, metric })
      }
    }
  }

  return columns
}

export const finaliseChoicesChart = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chartProp,
  firstAxis,
  secondAxis,
  otherSecondAxis,
  thirdAxis,
  choicesList,
  partialChoicesMap
}: {
  chartProp: ChartCardChartProps<DataKey, DataRow>
  firstAxis: FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow>
  secondAxis: FinaliseMetricsResult<DataKey, DataRow>
  otherSecondAxis: FinaliseMetricsResult<DataKey, DataRow>
  thirdAxis: FinaliseDimensionValuesResult<DataKey, DataRow>
  choicesList: DataKey[]
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): ChartCardChartDefinition<DataKey, DataRow> | undefined => {
  const chartType = chartProp.type

  switch (chartType) {
    case 'scatter':
      return finaliseChoicesScatterChart<DataKey, DataRow>({
        layout: chartProp.layout,
        choicesTogether: chartProp.choicesTogether ?? false,
        firstAxis,
        secondAxis,
        thirdAxis,
        customProps: chartProp.customProps,
        choicesList,
        partialChoicesMap
      })
    case 'pie':
    case 'donut':
      return finaliseChoicesPolarChart<DataKey, DataRow, PolarChartType>({
        chartType: chartProp.type,
        choicesTogether: chartProp.choicesTogether ?? false,
        firstAxis,
        secondAxis,
        customProps: chartProp.customProps,
        choicesList,
        partialChoicesMap
      })
    case 'bar':
    case 'line':
    case 'gradientLine':
    case 'area':
      return finaliseChoicesCartesianChart<DataKey, DataRow, CartesianChartType>({
        layout: chartProp.layout,
        chartType: chartProp.type,
        otherChartType: chartProp.otherType,
        choicesTogether: chartProp.choicesTogether ?? false,
        mainOther: chartProp.mainOther ?? false,
        firstAxis,
        secondAxis,
        otherSecondAxis,
        customProps: chartProp.customProps,
        otherCustomProps: chartProp.otherCustomProps,
        choicesList,
        partialChoicesMap
      })
    default:
      assertNever(chartType)
      return undefined
  }
}

export const finaliseChoicesScatterChart = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  layout,
  choicesTogether,
  firstAxis,
  secondAxis,
  thirdAxis,
  customProps,
  choicesList,
  partialChoicesMap
}: {
  layout: LayoutType | undefined
  choicesTogether: boolean
  firstAxis: FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow>
  secondAxis: FinaliseMetricsResult<DataKey, DataRow>
  thirdAxis: FinaliseDimensionValuesResult<DataKey, DataRow>
  customProps: Omit<DrawingInnerProps<ScatterChartType>, 'dataKey'> | undefined
  choicesList: DataKey[]
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): ChartCardChartDefinition<DataKey, DataRow> | undefined => {
  if (firstAxis.type === 'invalid' || secondAxis.type === 'invalid' || thirdAxis.type === 'invalid') {
    return undefined
  }

  if (firstAxis.type === 'undefined' || secondAxis.type === 'undefined' || thirdAxis.type === 'undefined') {
    return undefined
  }

  const chart: ChartCardChartDefinition<DataKey, DataRow> = {
    view: 'cartesian',
    dimensionAxis: 'third',
    type: 'scatter',
    layout,
    choicesTogether,
    firstAxis: firstAxis.id,
    secondAxis: secondAxis.id,
    thirdAxis: thirdAxis.id,
    customProps
  }

  populateChoices(firstAxis.id, firstAxis.choices, choicesList, partialChoicesMap)
  populateChoices(secondAxis.id, secondAxis.choices, choicesList, partialChoicesMap)
  populateChoices(thirdAxis.id, thirdAxis.choices, choicesList, partialChoicesMap)

  return chart
}

export const finaliseChoicesPolarChart = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Type extends PolarChartType
>({
  chartType,
  choicesTogether,
  firstAxis,
  secondAxis,
  customProps,
  choicesList,
  partialChoicesMap
}: {
  chartType: Type
  choicesTogether: boolean
  firstAxis: FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow>
  secondAxis: FinaliseMetricsResult<DataKey, DataRow>
  customProps: Omit<DrawingInnerProps<Type>, 'dataKey'> | undefined
  choicesList: DataKey[]
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): ChartCardChartDefinition<DataKey, DataRow> | undefined => {
  if (firstAxis.type === 'invalid' || secondAxis.type === 'invalid') {
    return undefined
  }

  if (firstAxis.type === 'undefined' || secondAxis.type === 'undefined') {
    return undefined
  }

  const chart: ChartCardChartDefinition<DataKey, DataRow> = {
    view: 'polar',
    dimensionAxis: 'first',
    type: chartType,
    choicesTogether,
    firstAxis: firstAxis.id,
    secondAxis: secondAxis.id,
    customProps
  }

  populateChoices(firstAxis.id, firstAxis.choices, choicesList, partialChoicesMap)
  populateChoices(secondAxis.id, secondAxis.choices, choicesList, partialChoicesMap)

  return chart
}

export const finaliseChoicesCartesianChart = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Type extends CartesianChartType
>({
  layout,
  chartType,
  otherChartType,
  choicesTogether,
  mainOther,
  firstAxis,
  secondAxis,
  otherSecondAxis,
  customProps,
  otherCustomProps,
  choicesList,
  partialChoicesMap
}: {
  layout: LayoutType | undefined
  chartType: Type
  otherChartType: CartesianChartType | undefined
  choicesTogether: boolean
  mainOther: boolean
  firstAxis: FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow>
  secondAxis: FinaliseMetricsResult<DataKey, DataRow>
  otherSecondAxis: FinaliseMetricsResult<DataKey, DataRow>
  customProps: Omit<DrawingInnerProps<Type>, 'dataKey'> | undefined
  otherCustomProps: Omit<DrawingInnerProps<Type>, 'dataKey'> | undefined
  choicesList: DataKey[]
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}): ChartCardChartDefinition<DataKey, DataRow> | undefined => {
  if (firstAxis.type === 'invalid' || secondAxis.type === 'invalid' || otherSecondAxis.type === 'invalid') {
    return undefined
  }

  if (firstAxis.type === 'undefined' || secondAxis.type === 'undefined') {
    return undefined
  }

  populateChoices(firstAxis.id, firstAxis.choices, choicesList, partialChoicesMap)

  const secondChoicesList: DataKey[] = []
  const otherSecondChoicesList: DataKey[] = []

  populateChoices(secondAxis.id, secondAxis.choices, secondChoicesList, partialChoicesMap)

  if (otherChartType == null) {
    if (otherSecondAxis.type !== 'undefined') {
      populateChoices(otherSecondAxis.id, otherSecondAxis.choices, otherSecondChoicesList, partialChoicesMap)
    }

    if (mainOther) {
      choicesList.push(...otherSecondChoicesList, ...secondChoicesList)
    } else {
      choicesList.push(...secondChoicesList, ...otherSecondChoicesList)
    }

    return {
      layout,
      view: 'cartesian',
      dimensionAxis: 'first',
      type: chartType,
      choicesTogether,
      mainOther,
      firstAxis: firstAxis.id,
      secondAxis: secondAxis.id,
      otherSecondAxis: otherSecondAxis.id,
      customProps,
      otherCustomProps
    }
  } else {
    if (otherSecondAxis.type === 'undefined') {
      return undefined
    }

    populateChoices(otherSecondAxis.id, otherSecondAxis.choices, otherSecondChoicesList, partialChoicesMap)

    if (mainOther) {
      choicesList.push(...otherSecondChoicesList, ...secondChoicesList)
    } else {
      choicesList.push(...secondChoicesList, ...otherSecondChoicesList)
    }

    return {
      layout,
      view: 'cartesian',
      dimensionAxis: 'first',
      type: chartType,
      otherType: otherChartType,
      choicesTogether,
      mainOther,
      firstAxis: firstAxis.id,
      secondAxis: secondAxis.id,
      otherSecondAxis: otherSecondAxis.id,
      customProps,
      otherCustomProps
    }
  }
}

const finaliseBasicElementsDescriptionsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>,
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
): void => {
  for (const [id, metric] of strictObjectEntries(metricsMap)) {
    const { type, aggregation, missingValue, label, description, color, pattern, format } = metric
    elementsDescriptionsMap[id] = {
      type,
      aggregation,
      missingValue,
      label,
      description,
      color,
      pattern,
      format: format as ElementDescriptionMetric['format']
    }
  }

  for (const [dimensionId, dimension] of strictObjectEntries(dimensionsMap)) {
    const { type, label, description, color, pattern, descriptions: valuesDescriptionsMap, format } = dimension
    elementsDescriptionsMap[dimensionId] = {
      type,
      label,
      description,
      color,
      pattern,
      descriptions: valuesDescriptionsMap,
      format: format as ElementDescriptionDimension['format']
    }
    if (type === 'variant') {
      const { newMetrics, variantsMap } = dimension
      for (const [newMetricId, newDescription] of strictObjectEntries(newMetrics)) {
        const firstVariantMetricId = variantsMap[Object.keys(variantsMap)[0]][newMetricId] as DataKey
        const { type, format } = metricsMap[firstVariantMetricId] as MetricChoice<DataKey, DataRow>
        elementsDescriptionsMap[newMetricId] = {
          type,
          format: format as ElementDescriptionMetric['format'],
          ...newDescription,
          variant: dimensionId
        }
      }
    }
  }
}

export const finaliseElementsDescriptionsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  metricsMap,
  dimensionsMap,
  dimensionsValuesMap,
  choicesMap
}: {
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
}): ElementsDescriptionsMap<DataKey> => {
  const elementsDescriptionsMap: ElementsDescriptionsMap<DataKey> = {}

  finaliseBasicElementsDescriptionsMap(dimensionsMap, metricsMap, elementsDescriptionsMap)

  for (const choice of strictObjectValues(choicesMap)) {
    finaliseChoiceElementsDescriptionsMap(choice, elementsDescriptionsMap)

    const { type } = choice

    switch (type) {
      case 'metricTransposed':
        generateAllTransposedMetricsDefinitions<DataKey, DataRow>({
          choice,
          metricsMap,
          dimensionsMap,
          dimensionsValuesMap,
          choicesMap,
          elementsDescriptionsMap
        })
        break
      case 'metric':
        if (choice.variant != null) {
          generateAllVariantMetricsDefinitions(choice, choicesMap, dimensionsMap, metricsMap, elementsDescriptionsMap)
        }
        break
      case 'metricVariants':
        if (choice.transpose == null) {
          generateAllVariantMetricsDefinitions(choice, choicesMap, dimensionsMap, metricsMap, elementsDescriptionsMap)
        } else {
          generateAllTransposedMetricsDefinitions<DataKey, DataRow>({
            choice: choice as ChartMetricVariantsTransposedChoice<DataKey, DataRow>,
            metricsMap,
            dimensionsMap,
            dimensionsValuesMap,
            choicesMap,
            elementsDescriptionsMap
          })
        }
        break
    }
  }

  return elementsDescriptionsMap
}

const finaliseChoiceElementsDescriptionsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice: ChartChoice<DataKey, DataRow>,
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
): void => {
  const { id, type, change, label, description } = choice
  const { options } = change
  let valuesdescriptionsMap: ElementDescription['descriptions'] | undefined
  let valuesDefinitionsOfMap: ElementDescription['descriptionsOfMap'] | undefined
  if (Array.isArray(options)) {
    valuesdescriptionsMap = options.reduce<ElementDescription['descriptions']>(
      (acc, { value, ...option }) => ({ ...acc, [value]: { ...option } }),
      {}
    )
  } else {
    valuesDefinitionsOfMap = strictObjectEntries(options).reduce<
      Record<string, Record<string, BaseElementDescription>>
    >(
      (acc, [optionOf, options]) => ({
        ...acc,
        [optionOf]: options.reduce<Record<string, BaseElementDescription>>(
          (acc, { value, ...option }) => ({
            ...acc,
            [value]: { ...option }
          }),
          {}
        )
      }),
      {}
    )
  }

  switch (type) {
    case 'metric':
    case 'metrics':
    case 'metricVariants':
    case 'metricTransposed': {
      const elementDescription: ElementDescriptionMetricChoice = {
        type,
        label,
        description,
        descriptions: valuesdescriptionsMap,
        descriptionsOfMap: valuesDefinitionsOfMap,
        variant: choice.variant
      }
      elementsDescriptionsMap[id] = elementDescription
      break
    }
    case 'value':
    case 'values':
    case 'valuesOf':
    case 'dimension':
    case 'valueOf':
    case 'variantValue':
    case 'variantValues':
    case TableColumnValuesChoice: {
      const elementDescription: ElementDescriptionDimensionChoice = {
        type,
        label,
        description,
        descriptions: valuesdescriptionsMap,
        descriptionsOfMap: valuesDefinitionsOfMap
      }
      elementsDescriptionsMap[id] = elementDescription
      break
    }
    case TableColumnsChoice:
      // Nothing to do here, tables columns are handled by a custom component
      break
    default:
      assertNever(type)
  }
}
